import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Search, RefreshCw, ExternalLink } from 'lucide-react';

const OutstandingDashboard = () => {
  const [entityType, setEntityType] = useState('customer');
  const [mappings, setMappings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [showTransactions, setShowTransactions] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => { fetchData(); }, [entityType]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/config/outstanding-summary?entity_type=${entityType}`);
      setSummary(res.data);
      setMappings(res.data.mappings || []);
    } catch (err) { console.error('Failed to fetch:', err); }
    finally { setLoading(false); }
  };

  const viewTransactions = async (mapping) => {
    setSelectedEntity(mapping);
    try {
      const res = await api.get(`/config/transactions/${mapping.entity_type}/${mapping.entity_id}`);
      setTransactions(res.data);
    } catch (err) { setTransactions([]); }
    setShowTransactions(true);
  };

  const syncOutstanding = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/config/sync-outstanding');
      alert(res.data.message);
    } catch (err) { alert(err.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const autoCreateLedger = async (mapping) => {
    try {
      const res = await api.post(`/config/auto-create-ledger/${mapping.entity_type}/${mapping.entity_id}`);
      alert(res.data.message);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const filtered = mappings.filter(m =>
    m.entity_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.entity_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.tally_alias.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalOutstanding = summary?.total_outstanding || 0;

  const txnTypeBadge = (type) => {
    const map = {
      sales_order: 'badge-info', invoice: 'badge-warning', payment: 'badge-success',
      receipt: 'badge-success', credit_note: 'badge-danger', debit_note: 'badge-danger',
      purchase_order: 'badge-info'
    };
    return `badge ${map[type] || 'badge-info'}`;
  };

  return (
    <div data-testid="outstanding-dashboard">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Outstanding Dashboard</h1>
        <button onClick={syncOutstanding} className="btn btn-secondary" disabled={syncing} data-testid="sync-btn">
          <RefreshCw size={16} className={syncing ? 'spinning' : ''} />
          {syncing ? 'Syncing...' : 'Sync from Tally'}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3" style={{ gap: '16px', marginBottom: '16px' }}>
        <div className="card" style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ fontSize: '28px', fontWeight: 700, color: totalOutstanding >= 0 ? '#DC2626' : '#16A34A' }} data-testid="total-outstanding">
            {totalOutstanding.toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 })}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px' }}>TOTAL OUTSTANDING</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ fontSize: '28px', fontWeight: 700 }} data-testid="entity-count">{summary?.entity_count || 0}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px' }}>MAPPED {entityType === 'customer' ? 'CUSTOMERS' : 'VENDORS'}</div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ fontSize: '28px', fontWeight: 700 }}>{mappings.filter(m => m.outstanding_amount > 0).length}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px' }}>WITH BALANCE DUE</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="grid grid-cols-2" style={{ gap: '16px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <div style={{ position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
              <input type="text" className="form-input" placeholder="Search by name, code, or alias..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <select className="form-select" value={entityType} onChange={(e) => setEntityType(e.target.value)} data-testid="entity-type-filter">
              <option value="customer">Customers</option>
              <option value="vendor">Vendors</option>
            </select>
          </div>
        </div>
      </div>

      {/* Mappings Table */}
      <div className="card">
        {loading ? <div data-testid="loading-indicator">Loading...</div> : (
          <div className="table-container">
            <table>
              <thead>
                <tr><th>Code</th><th>Name</th><th>Tally Alias</th><th>Tally Ledger</th><th className="text-right">Outstanding</th><th>Last Synced</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: '32px' }}>
                    No ledger mappings found. Create mappings from Customer/Vendor pages or use "Auto Create Ledger".
                  </td></tr>
                ) : (
                  filtered.map(m => (
                    <tr key={m.id} data-testid={`mapping-row-${m.entity_code}`}>
                      <td className="font-mono">{m.entity_code}</td>
                      <td>{m.entity_name}</td>
                      <td className="font-mono text-sm">{m.tally_alias}</td>
                      <td className="text-sm">{m.tally_ledger_name || '-'}</td>
                      <td className="text-right tabular-nums" style={{ fontWeight: 600, color: m.outstanding_amount > 0 ? '#DC2626' : 'inherit' }}>
                        {m.outstanding_amount.toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 })}
                      </td>
                      <td className="text-sm">{m.last_synced_at ? new Date(m.last_synced_at).toLocaleDateString() : 'Never'}</td>
                      <td>
                        <button onClick={() => viewTransactions(m)} className="btn btn-ghost btn-sm" title="View Transactions" data-testid={`view-btn-${m.entity_code}`}>
                          <ExternalLink size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ marginTop: '12px', fontSize: '12px', color: 'var(--text-tertiary)', padding: '0 4px' }}>
        Outstanding amounts and transactions will be synced from Tally once connected. Customer/Vendor code is used as the Tally alias for ledger lookup.
      </div>

      {/* Transaction Detail Modal */}
      {showTransactions && selectedEntity && (
        <div className="modal-overlay" data-testid="txn-modal">
          <div className="modal-content" style={{ maxWidth: '800px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>
                {selectedEntity.entity_name} — Ledger
              </h2>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', gap: '24px', marginBottom: '16px', padding: '12px', background: 'var(--bg-surface-alt)', borderRadius: '4px' }}>
                <div><span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Code:</span> <strong>{selectedEntity.entity_code}</strong></div>
                <div><span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Tally Alias:</span> <strong>{selectedEntity.tally_alias}</strong></div>
                <div><span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Outstanding:</span> <strong style={{ color: '#DC2626' }}>{selectedEntity.outstanding_amount.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</strong></div>
              </div>

              {transactions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-tertiary)' }}>
                  No transactions yet. Transactions will appear here once synced from Tally.
                  <br /><br />
                  <strong>For customers:</strong> Sales Orders, Invoices, Payments, Credit Notes
                  <br />
                  <strong>For vendors:</strong> Purchase Orders, Invoices, Payments, Debit Notes
                </div>
              ) : (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr><th>Date</th><th>Type</th><th>Number</th><th>Tally Voucher</th><th className="text-right">Amount</th><th className="text-right">Balance</th></tr>
                    </thead>
                    <tbody>
                      {transactions.map(txn => (
                        <tr key={txn.id}>
                          <td className="text-sm">{new Date(txn.transaction_date).toLocaleDateString()}</td>
                          <td><span className={txnTypeBadge(txn.transaction_type)}>{txn.transaction_type.replace(/_/g, ' ')}</span></td>
                          <td className="font-mono">{txn.transaction_number}</td>
                          <td className="font-mono text-sm">{txn.tally_voucher_number || '-'}</td>
                          <td className="text-right tabular-nums">{txn.amount.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</td>
                          <td className="text-right tabular-nums">{txn.balance.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowTransactions(false)} className="btn btn-secondary">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutstandingDashboard;
